import type { LucideIcon } from 'lucide-react';
import {
  Award,
  TrendingUp,
  Coins,
  Trophy,
  FileCheck2,
  GraduationCap,
  Flame,
  ShieldAlert,
  PhoneOff,
  TrendingDown,
  BookOpen,
  Clock,
  Hourglass,
  Gauge,
} from 'lucide-react';

export type CelebrationTone = 'happy' | 'info';

export type CelebrationKind =
  | 'badge_earned'
  | 'level_up'
  | 'points_awarded'
  | 'rank_climbed'
  | 'certificate_received'
  | 'training_completed'
  | 'streak_milestone'
  | 'phishing_compromised'
  | 'vishing_failed'
  | 'rank_dropped'
  | 'training_assigned'
  | 'training_due_soon'
  | 'training_expired'
  | 'risk_level_increased';

interface BaseEvent {
  id: string;
  kind: CelebrationKind;
}

export type CelebrationEvent =
  | (BaseEvent & { kind: 'badge_earned'; badgeName: string; badgeIcon?: string; eventDescription?: string; tier?: 'bronze' | 'silver' | 'gold' | 'platinum' })
  | (BaseEvent & { kind: 'level_up'; levelName: string; levelIcon?: string; pointsRequired: number; percentile?: number })
  | (BaseEvent & { kind: 'points_awarded'; points: number; activity: string; totalPoints: number })
  | (BaseEvent & { kind: 'rank_climbed'; rank: number; delta: number; nextRankName?: string; gap?: number })
  | (BaseEvent & { kind: 'certificate_received'; courseTitle: string; certificateImage?: string })
  | (BaseEvent & { kind: 'training_completed'; courseTitle: string; courseImage?: string; points?: number })
  | (BaseEvent & { kind: 'streak_milestone'; count: number })
  | (BaseEvent & { kind: 'phishing_compromised'; campaignName: string })
  | (BaseEvent & { kind: 'vishing_failed'; campaignName?: string })
  | (BaseEvent & { kind: 'rank_dropped'; rank: number; delta: number })
  | (BaseEvent & { kind: 'training_assigned'; courseTitle: string; courseImage?: string; dueDate: string; minutes?: number })
  | (BaseEvent & { kind: 'training_due_soon'; courseTitle: string; courseImage?: string; daysLeft: number })
  | (BaseEvent & { kind: 'training_expired'; courseTitle: string; courseImage?: string })
  | (BaseEvent & { kind: 'risk_level_increased'; level: 'High' | 'Medium' | 'Low' });

export interface CelebrationCopy {
  tone: CelebrationTone;
  headline: string;
  body: string;
  metric?: string;
  ctaLabel: string;
  ctaHref?: string;
  secondaryLabel?: string;
  icon: LucideIcon;
  accent: string;
  priority: number;
}

export const celebrationPriority: Record<CelebrationKind, number> = {
  level_up: 100,
  badge_earned: 90,
  certificate_received: 85,
  rank_climbed: 75,
  training_completed: 70,
  streak_milestone: 65,
  points_awarded: 50,
  risk_level_increased: 48,
  phishing_compromised: 45,
  vishing_failed: 44,
  training_expired: 42,
  rank_dropped: 40,
  training_due_soon: 30,
  training_assigned: 20,
};

export function describeCelebration(event: CelebrationEvent): CelebrationCopy {
  switch (event.kind) {
    case 'badge_earned':
      return {
        tone: 'happy',
        headline: 'New badge unlocked!',
        body: `You earned **${event.badgeName}**${event.eventDescription ? ` — ${event.eventDescription}` : ''}. Keep it going.`,
        ctaLabel: 'View badge',
        ctaHref: '#badges',
        icon: Award,
        accent: tierAccent(event.tier),
        priority: celebrationPriority.badge_earned,
      };
    case 'level_up':
      return {
        tone: 'happy',
        headline: `Level up — ${event.levelName}!`,
        body: `You crossed **${event.pointsRequired} pts**.${event.percentile ? ` Ahead of ${event.percentile}% of your team.` : ''}`,
        ctaLabel: 'See your level',
        ctaHref: '#points',
        icon: TrendingUp,
        accent: 'from-violet-500/20 to-fuchsia-500/10 text-violet-500',
        priority: celebrationPriority.level_up,
      };
    case 'points_awarded':
      return {
        tone: 'happy',
        headline: `+${event.points} points`,
        body: `For **${event.activity}**.`,
        metric: `Total: ${event.totalPoints} pts`,
        ctaLabel: 'Nice',
        icon: Coins,
        accent: 'from-amber-500/20 to-yellow-500/10 text-amber-500',
        priority: celebrationPriority.points_awarded,
      };
    case 'rank_climbed':
      return {
        tone: 'happy',
        headline: `You climbed to #${event.rank}!`,
        body: `Up **${event.delta}** spots on the leaderboard.${event.nextRankName && event.gap ? ` ${event.nextRankName} is ${event.gap} pts away.` : ''}`,
        ctaLabel: 'See leaderboard',
        ctaHref: '#leaderboard',
        icon: Trophy,
        accent: 'from-green-500/20 to-emerald-500/10 text-green-500',
        priority: celebrationPriority.rank_climbed,
      };
    case 'certificate_received':
      return {
        tone: 'happy',
        headline: 'Certificate earned',
        body: `You completed **${event.courseTitle}**. Your certificate is ready.`,
        ctaLabel: 'Download',
        ctaHref: '#certificates',
        secondaryLabel: 'Later',
        icon: FileCheck2,
        accent: 'from-blue-500/20 to-cyan-500/10 text-blue-500',
        priority: celebrationPriority.certificate_received,
      };
    case 'training_completed':
      return {
        tone: 'happy',
        headline: 'Training complete',
        body: `**${event.courseTitle}** done.${event.points ? ` +${event.points} pts, one step closer to your next level.` : ''}`,
        ctaLabel: 'Keep going',
        ctaHref: '#trainings',
        icon: GraduationCap,
        accent: 'from-green-500/20 to-emerald-500/10 text-green-500',
        priority: celebrationPriority.training_completed,
      };
    case 'streak_milestone':
      return {
        tone: 'happy',
        headline: `${event.count}-training streak!`,
        body: `You've completed ${event.count} trainings on time in a row. Rare air.`,
        ctaLabel: 'Keep the streak',
        ctaHref: '#trainings',
        icon: Flame,
        accent: 'from-orange-500/20 to-red-500/10 text-orange-500',
        priority: celebrationPriority.streak_milestone,
      };
    case 'phishing_compromised':
      return {
        tone: 'info',
        headline: 'A learning moment',
        body: `You interacted with a simulated phishing attempt — **${event.campaignName}**. No real harm done. We've queued a short refresher to lock this in.`,
        ctaLabel: 'Start refresher',
        ctaHref: '#trainings',
        secondaryLabel: 'Later',
        icon: ShieldAlert,
        accent: 'from-amber-500/20 to-orange-500/10 text-amber-500',
        priority: celebrationPriority.phishing_compromised,
      };
    case 'vishing_failed':
      return {
        tone: 'info',
        headline: 'That call was a simulation',
        body: `It's designed to be hard${event.campaignName ? ` — **${event.campaignName}**` : ''}. Here's what to listen for next time.`,
        ctaLabel: 'See the tells',
        ctaHref: '#trainings',
        secondaryLabel: 'Later',
        icon: PhoneOff,
        accent: 'from-amber-500/20 to-orange-500/10 text-amber-500',
        priority: celebrationPriority.vishing_failed,
      };
    case 'rank_dropped':
      return {
        tone: 'info',
        headline: "Your team's moving fast",
        body: `You slipped **${event.delta}** spots. One completed training closes that gap.`,
        ctaLabel: 'Pick a training',
        ctaHref: '#trainings',
        secondaryLabel: 'Later',
        icon: TrendingDown,
        accent: 'from-slate-500/20 to-zinc-500/10 text-slate-500',
        priority: celebrationPriority.rank_dropped,
      };
    case 'training_assigned':
      return {
        tone: 'info',
        headline: 'New training assigned',
        body: `**${event.courseTitle}** — due ${event.dueDate}.${event.minutes ? ` ~${event.minutes} min.` : ''}`,
        ctaLabel: 'Start now',
        ctaHref: '#trainings',
        secondaryLabel: 'Later',
        icon: BookOpen,
        accent: 'from-blue-500/20 to-sky-500/10 text-blue-500',
        priority: celebrationPriority.training_assigned,
      };
    case 'training_due_soon':
      return {
        tone: 'info',
        headline: `Due in ${event.daysLeft} ${event.daysLeft === 1 ? 'day' : 'days'}`,
        body: `**${event.courseTitle}** is waiting. Knock it out now?`,
        ctaLabel: 'Start',
        ctaHref: '#trainings',
        secondaryLabel: 'Later',
        icon: Clock,
        accent: 'from-amber-500/20 to-yellow-500/10 text-amber-500',
        priority: celebrationPriority.training_due_soon,
      };
    case 'training_expired':
      return {
        tone: 'info',
        headline: 'Training expired',
        body: `**${event.courseTitle}** lapsed. You can still complete it to claim the points.`,
        ctaLabel: 'Complete now',
        ctaHref: '#trainings',
        secondaryLabel: 'Later',
        icon: Hourglass,
        accent: 'from-red-500/20 to-rose-500/10 text-red-500',
        priority: celebrationPriority.training_expired,
      };
    case 'risk_level_increased':
      return {
        tone: 'info',
        headline: "Let's bring your risk score down",
        body: `Your risk level moved to **${event.level}**. Two trainings this week would flip it back.`,
        ctaLabel: 'View recommended',
        ctaHref: '#trainings',
        secondaryLabel: 'Later',
        icon: Gauge,
        accent: 'from-amber-500/20 to-red-500/10 text-amber-500',
        priority: celebrationPriority.risk_level_increased,
      };
  }
}

function tierAccent(tier?: 'bronze' | 'silver' | 'gold' | 'platinum'): string {
  switch (tier) {
    case 'platinum':
      return 'from-cyan-400/30 to-blue-500/10 text-cyan-400';
    case 'gold':
      return 'from-yellow-400/30 to-amber-500/10 text-yellow-500';
    case 'silver':
      return 'from-slate-300/30 to-zinc-400/10 text-slate-400';
    case 'bronze':
      return 'from-orange-400/30 to-amber-600/10 text-orange-500';
    default:
      return 'from-green-500/20 to-emerald-500/10 text-green-500';
  }
}
